/*
 * Site settings: the knowledge base's own name and face.
 *
 * One row in kb.SiteSettings - title, subtitle and an optional icon, and since
 * 2026-10-15 the release-note list's own title and subtitle and the articles
 * page's own title - edited on the admin Settings page and read by every public
 * page load. What lives here is
 * the part both ends have to agree on: the defaults the row is seeded with, the
 * lengths the columns allow, the icon's size cap, and the address the icon is
 * served from.
 *
 * The icon is an image like any article image - same four content types, same
 * magic-byte sniffing, same "never trust the declared type" gate - so nothing
 * about FORMAT is restated here; articleImages.ts owns it. What differs is the
 * cap: 1 MB rather than 5, because the icon is fetched by every reader and drawn
 * at 28 pixels, and the cap is on kb.SiteSettings as a CHECK as well as here.
 */
/**
 * The defaults the database row is seeded with, in
 * datamodel/.../Scripts/Seed.SiteSettings.sql. Duplicated here on purpose, and
 * kept identical: the application falls back to these if the row is absent, and
 * a fallback that differs from the seed would show a different name depending on
 * whether the post-deployment script had run.
 */
export const DEFAULT_SITE_TITLE = 'accoTOOL Knowledge Base';
export const DEFAULT_SITE_SUBTITLE = 'Guides, references and known issues.';
/**
 * The release-note list's heading and the line under it (2026-10-15): the
 * DEFAULTs of kb.SiteSettings.ReleaseNotesTitle and ReleaseNotesSubtitle, which
 * are the text the page showed before either could be configured. Duplicated
 * and kept identical for the reason above.
 */
export const DEFAULT_RELEASE_NOTES_TITLE = 'Release Notes';
export const DEFAULT_RELEASE_NOTES_SUBTITLE = 'What changed in each version, newest first.';
/**
 * The articles page's own heading (2026-10-15): the DEFAULT of
 * kb.SiteSettings.ArticlesTitle, which is the shipped SITE title, because until
 * then the site title was that heading - so the column comes in showing what
 * the page showed. Its line is the existing subtitle, DEFAULT_SITE_SUBTITLE.
 */
export const DEFAULT_ARTICLES_TITLE = DEFAULT_SITE_TITLE;
/**
 * kb.SiteSettings.Title is nvarchar(200); Subtitle is nvarchar(500). The
 * release-notes pair has the same two widths, so these bound it too.
 */
export const SITE_TITLE_MAX_LENGTH = 200;
export const SITE_SUBTITLE_MAX_LENGTH = 500;
/**
 * CK_SiteSettings_IconByteLength: 1 MB. A fifth of the article-image cap, for
 * the reason in the file header.
 */
export const SITE_ICON_MAX_BYTES = 1024 * 1024;
export function describeSiteIconSizeLimit() {
    return 'The icon must be 1 MB or smaller.';
}
/**
 * The public address of the icon bytes, with the /api prefix in full because it
 * ends up in an <img src> the browser loads directly.
 *
 * The content hash rides along as a query parameter, for the CACHE: the endpoint
 * sends a day of max-age when - and only when - the hash in the request matches
 * the icon it holds, so a new icon (new hash, new URL) is fetched fresh and the
 * old URL is simply never asked for again. A request whose hash does not match,
 * or carries none, gets the ordinary short public policy instead; the server
 * checks rather than trusts, so a stale URL can never be paired with the wrong
 * bytes for a day. Sixteen hex characters of the SHA-256 is plenty to tell two
 * icons apart and keeps the URL short.
 */
export const SITE_ICON_PUBLIC_PATH = '/api/public/site/icon';
export function siteIconUrl(contentHash) {
    return `${SITE_ICON_PUBLIC_PATH}?v=${encodeURIComponent(contentHash.slice(0, 16))}`;
}
//# sourceMappingURL=siteSettings.js.map